import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';

// Setup → Promotions. Move-in discounts: percent / flat / free_period.
const TYPES = [
  { value: 'percent', label: '% off first month' },
  { value: 'flat', label: '$ off first month' },
  { value: 'free_period', label: 'free months' },
];
const blank = {
  name: '', type: 'percent', value: '', code: '',
  applies_to: 'all', active: true,
};

const Promotions = () => {
  const { facilityId } = useFacility();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/api/facilities/${facilityId}/promotions/`);
      setRows(r.data);
    } catch {
      setError('Could not load promotions');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      name: form.name.trim(),
      type: form.type,
      value: parseFloat(form.value),
      code: form.code.trim() || null,
      applies_to: form.applies_to.trim() || 'all',
      active: !!form.active,
    };
    if (!payload.name || Number.isNaN(payload.value)) {
      setError('Name and a numeric value are required');
      return;
    }
    try {
      if (editing === 'new') {
        await apiClient.post(`/api/facilities/${facilityId}/promotions/`, payload);
      } else {
        await apiClient.put(
          `/api/facilities/${facilityId}/promotions/${editing}`, payload);
      }
      setEditing(null);
      await load();
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promotion "${p.name}"?`)) return;
    try {
      await apiClient.delete(`/api/facilities/${facilityId}/promotions/${p._id}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const input =
    'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500';
  const fmtVal = (p) =>
    p.type === 'percent' ? `${p.value}% off`
      : p.type === 'flat' ? `$${p.value} off`
      : `${p.value} mo free`;

  return (
    // Rendered embedded inside the Units page (Promotions tab). The page H1
    // + tab strip come from UnitsPage, so this view drops its own page title
    // / eyebrow and the outer max-width — it just renders the action bar +
    // table so it looks native inside the tab.
    <div>
      <div className="mb-5 flex items-start justify-between">
        <p className="text-[13px] text-slate-500">
          Move-in discounts. Applied to a rental when it's created.
        </p>
        <button
          onClick={() => { setForm(blank); setEditing('new'); setError(''); }}
          className="inline-flex items-center gap-1.5 py-2 px-3.5 rounded-lg bg-indigo-600 text-white text-[13px] font-semibold shadow-sm hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-4 w-4" /> New promotion
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200/80 px-3 py-2.5 text-[13px] text-rose-700">
          {error}
        </div>
      )}

      {editing && (
        <form onSubmit={save}
          className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Name</label>
            <input className={input} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="First month 50% off" />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Type</label>
            <select className={input} value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Value</label>
            <input className={input} type="number" step="0.01" value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              placeholder={form.type === 'free_period' ? '1' : '50'} />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Code (optional)</label>
            <input className={input} value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="HALFOFF" />
          </div>
          <label className="flex items-center gap-2 text-[13px] text-slate-700 sm:col-span-2">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active
          </label>
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit"
              className="py-2 px-4 rounded-lg bg-indigo-600 text-white text-[13px] font-semibold hover:bg-indigo-700">
              Save
            </button>
            <button type="button" onClick={() => setEditing(null)}
              className="py-2 px-4 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-slate-50/60 text-left">
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Name</th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Discount</th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Code</th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">No promotions yet</td></tr>
              ) : rows.map((p) => (
                <tr key={p._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3.5 font-medium text-slate-900">{p.name}</td>
                  <td className="px-5 py-3.5 text-slate-600">{fmtVal(p)}</td>
                  <td className="px-5 py-3.5 text-slate-500">
                    {p.code
                      ? <span className="inline-flex items-center gap-1 font-mono text-[12px] bg-slate-100 px-1.5 py-0.5 rounded"><Tag className="h-3 w-3" />{p.code}</span>
                      : '—'}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
                      p.active
                        ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
                        : 'bg-slate-100 text-slate-500 ring-slate-500/20'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${p.active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                      {p.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => {
                        setForm({
                          name: p.name, type: p.type, value: p.value,
                          code: p.code || '', applies_to: p.applies_to || 'all',
                          active: p.active,
                        });
                        setEditing(p._id); setError('');
                      }} className="p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(p)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Promotions;
